import Table, { TableBar } from "../common/Table";
import Typography from "../common/Typography";
import Flow from "../common/Flow";
import Button, { ButtonGroup } from "../common/Button";
import { hasMarketplace, isJumpGate, atWaypoint, canExtract, isHQ, hasAnyCargo, canDeliver, isInOrbit, isShipAction, isInTransit } from "../../utils/helpers";
import Select from "../common/Select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileContract, faGasPump, faKipSign, faLocationArrow, faPersonDolly, faPickaxe, faRadar, faRobotAstromech, faTelescope } from "@fortawesome/pro-light-svg-icons";
import ShipType from "./ShipType";
import { styled } from "styled-components";
import PieLoader from "../common/PieLoader";

const ShipDetailActions = styled.div`
  display: flex;
  justify-content: space-between;
`;

function ShipDetail({ store, innerRef }) {
  const ship = store.getSelectedShip();
  const waypoint = store.state.waypoint;
  const contracts = store.state.contracts;
  const gateInfo = waypoint && waypoint !== null ? store.getConnectedSystems(waypoint.symbol) : null;

  const changeFlightMode = (value) => {
    store.setFlightMode(value);
  }

  const navigateHere = () => {
    const ship = store.getSelectedShip();
    const waypoint = store.state.waypoint;

    if (ship && waypoint) {
      store.navigateTo(ship.symbol, waypoint.symbol);
    }
  };

  const changeStatus= (value) => {
    if (value == 'DOCKED') {
      store.dock(ship.symbol);
    }
    if (value == 'IN_ORBIT') {
      store.orbit(ship.symbol);
    }
  }

  const refuel = () => {
    store.refuel(waypoint);
  };

  const scanGate = () => {
    store.scanJumpGate(waypoint);
  };

  const extract = () => {
    store.extract();
  };

  const doSurvey = () => {
    store.survey();
  };

  const getContract = () => {
    store.getContract();
  };

  return (
    <Flow $space={3} ref={innerRef}>
      <Typography as="h2" fontSize="xl">{ship && ship.autopilot && <FontAwesomeIcon icon={faRobotAstromech} />} {ship && ship.symbol}</Typography>
      <Table>
        <tbody>
          <tr>
            <th>Status</th>
            <td>
              <Select $action onChange={(e) => changeStatus(e.target.value)} selected={ship && ship.nav.status} disabled={ship && (ship.nav.status === 'IN_TRANSIT' || ship.action !== undefined)}>
                <option value="DOCKED">Docked</option>
                <option value="IN_ORBIT">In orbit</option>
              </Select>
            </td>
          </tr>
          <tr>
            <th>Flight mode</th>
            <td>
              <Select $action onChange={(e) => changeFlightMode(e.target.value)} value={ship && ship.nav.flightMode} disabled={ship && (ship.nav.status === 'IN_TRANSIT' || ship.action !== undefined)}>
                <option value="CRUISE">Cruise</option>
                <option value="STEALTH">Stealth</option>
                <option value="BURN">Burn</option>
                <option value="DRIFT">Drift</option>
              </Select>
            </td>
          </tr>
          <tr>
            <th>Location</th>
            <td>{ship && ship.nav.waypointSymbol}</td>
          </tr>
          <tr>
            <th>Frame</th>
            <td>{ship && ship.frame.name}</td>
          </tr>
          <tr>
            <th className="middle">Fuel</th>
            <td>
              <TableBar title={`${ship && ship.fuel.current}/${ship && ship.fuel.capacity}`} $current={ship && ship.fuel.current} $max={ship && ship.fuel.capacity} />
            </td>
          </tr>
          <tr className="action" onClick={() => store.setModal('ship', 'cargo')}>
            <th className="middle">Cargo</th>
            <td>
              <TableBar $current={ship && ship.cargo.units} $max={ship && ship.cargo.capacity} />
            </td>
          </tr>
        </tbody>
      </Table>

      <ShipDetailActions>
        <ButtonGroup>
          <Button $icon className="split" onClick={() => store.setModal('ship')} title="View ship">
            <ShipType ship={ship} size="lg" />
          </Button>
          <Button $action="icon" onClick={() => store.shipScanSystems(ship.symbol)}>
            <FontAwesomeIcon icon={faRadar} />
          </Button>
          {waypoint &&
            <>
              {hasMarketplace(waypoint && waypoint.traits) && atWaypoint(ship, waypoint) &&
                <Button $action="icon" $disabled={ship.autopilot || isInOrbit(ship && ship) || isInTransit(ship && ship)} title={`Refuel ${ship.symbol}`} onClick={refuel}>
                  <FontAwesomeIcon size="lg" icon={faGasPump} />
                </Button>
              }
              {hasMarketplace(waypoint && waypoint.traits) && hasAnyCargo(ship) &&
                <Button $action="icon" title="Sell all cargo" $disabled={ship.autopilot || !atWaypoint(ship, waypoint) || isInOrbit(ship && ship) || isInTransit(ship)} onClick={store.sellAll}>
                  <FontAwesomeIcon size="lg" icon={faKipSign} />
                </Button>
              }
              {canDeliver(contracts, waypoint, ship) && atWaypoint(ship, waypoint) &&
                <Button $action="icon" $disabled={ship.autopilot || isInOrbit(ship && ship) || isInTransit(ship)} title="Deliver contract cargo" onClick={ () => store.deliverCargo() }>
                  <FontAwesomeIcon size="lg" icon={faPersonDolly} />
                </Button>
              }
              {isHQ(waypoint, store.state.agent) && atWaypoint(ship, waypoint) &&
                <Button $action="icon" $disabled={ship.autopilot || isInOrbit(ship && ship) || isInTransit(ship)} title="Get a contract" onClick={getContract}>
                  <FontAwesomeIcon size="lg" icon={faFileContract} />
                </Button>
              }
              {isJumpGate(waypoint) && atWaypoint(ship, waypoint) &&
                <Button $action="icon" title="Scan other systems" $disabled={ship.autopilot || gateInfo !== null} onClick={scanGate}>
                  <FontAwesomeIcon size="lg" icon={faTelescope} />
                </Button>
              }
              {canExtract(waypoint.traits) && atWaypoint(ship, waypoint) &&
                <Button $action="icon" $disabled={ship.autopilot || !isInOrbit(ship && ship) || isShipAction(ship, 'SURVEY')} title={`Survey ${waypoint.symbol}`} onClick={doSurvey}>
                  {ship && ship.action && ship.action.type == 'SURVEY' && <PieLoader duration={ship.action.duration} />}
                  <FontAwesomeIcon size="lg" icon={faTelescope} />
                </Button>
              }
              {canExtract(waypoint.traits) && atWaypoint(ship, waypoint) &&
                <Button $action="icon" $disabled={ship.autopilot || !isInOrbit(ship && ship) || isShipAction(ship, 'EXTRACT')} title={`Extract from ${waypoint.symbol}`} onClick={extract}>
                  {ship && ship.action && ship.action.type == 'EXTRACT' && <PieLoader duration={ship.action.duration} />}
                  <FontAwesomeIcon size="lg" icon={faPickaxe} />
                </Button>
              }
              {ship && !atWaypoint(ship, waypoint) &&
                <Button $action="icon" $disabled={ship.autopilot || !isInOrbit(ship && ship) || isShipAction(ship, 'NAVIGATE')} title={`Navigate to ${waypoint.symbol}`} type="submit" onClick={navigateHere}>
                  {ship && ship.action && ship.action.type == 'NAVIGATE' && <PieLoader duration={ship.action.duration} />}
                  <FontAwesomeIcon size="lg" icon={faLocationArrow} />
                </Button>
              }
            </>
          }
        </ButtonGroup>
      </ShipDetailActions>
    </Flow>
  );
}

export default ShipDetail;
